// balance-sync.ts — the balance the board shows (SC-62290).
//
// The vendored game animates a round with its own arithmetic: the stake leaves the shown balance when the roll starts
// (driven by the RGS: when the server's answer arrives), and the server's payout comes in when the roll lands. That is
// right for the round and blind to everything else: a deposit, a bet placed in another tab. Until SDK 0.12.0 nothing safe could tell the game about those — a re-read of the balance
// was not ordered against rounds, so a balance sampled before a round could land after it. Now the SDK orders its
// reads (`refreshBalance()`), and what it holds is the server's. The rule here:
//
//   whenever NO round is on screen, the board shows the balance the SDK holds.
//
//   balance_after = balance_before − stake + payout      (a round: the game's animation, the server's numbers)
//   balance_after = balance_before + deposit             (the wallet, outside any round: only the server knows)
//
// - It is applied only while the game is not rolling (`S.rolling`: from the click until the roll has landed). While a
//   round is in flight or travelling the board is the game's; the SDK already holds the settled balance once the
//   answer is in, and writing it early would give the result away and then let the game's own debit and credit count
//   the round a second time. So it is applied when the roll lands — the game takes
//   `data-rolling` off #frame after it credited the payout — and at once when a balance arrives while idle. Applied
//   then, a deposit made during a roll and the roll's own settlement each change the shown balance exactly once.
// - "No round on screen" includes the gap between two rolls of an autobet run. The run decided its next stake against
//   the balance the game's arithmetic left; when the server's balance no longer covers that stake the run is ended
//   ("Out of funds", the game's own words for it) instead of being left running with a roll its gate will refuse.
// - A balance the SDK could not confirm is never applied: a round that settled with no balance on the wire and whose
//   re-read failed leaves the SDK holding the balance from BEFORE the round (`balanceStale`). The game's own arithmetic
//   is the better number until a balance lands, and the server is asked again.
// - The server is asked when the wallet may have changed without a round: the page becomes visible again, the window
//   gets the focus back (the player returns from the cashier), the page comes back from the back/forward cache, and
//   when the game says the stake is above the shown balance. At most once every ASK_EVERY_MS: the SDK never sends a
//   read while a round is in flight and shares one read among callers, but a player with no funds must not turn every
//   drag of the stake into a wallet call.
//
// The unknown-outcome stop (round-outcome.ts) is untouched: a re-read says what the wallet holds, not what became of
// a round nobody heard back from.

/** The game, as far as its balance goes. */
export interface BalanceSyncGame {
  /** `auto`: the autobet run, `null` when there is none. */
  readonly S: { balance: number; rolling: boolean; stake: number; auto: unknown };
  paint(): void;
  /** Ends the autobet run with a reason the game shows and announces; it repaints. */
  stopAuto(why: string): void;
}

/** What this needs of the SDK (0.12.0). */
export interface BalanceSyncSdk {
  getBalance(): { readonly amountMinor: number } | null;
  refreshBalance(): Promise<boolean>;
  on(event: "balance.updated", listener: (payload: { readonly balanceMinor: number; readonly source: "round" | "read" }) => void): void;
}

export interface BalanceSync {
  /** A round settled: `balanceStale` when the SDK could not confirm the balance it reports for it. */
  roundSettled(round: { readonly balanceStale?: true }): void;
  /** The wallet may hold something else than the board shows: ask the server (at most once every ASK_EVERY_MS). */
  ask(): void;
}

/** The least time between two questions to the server that the game itself raises. */
export const ASK_EVERY_MS = 3_000;

export function syncBalance(
  doc: Document,
  win: Window & typeof globalThis,
  game: BalanceSyncGame,
  sdk: BalanceSyncSdk,
  // toGame: the SDK's balance (the wire's minor units) in the game's hundredths (SC-68493, currency.ts); absent: the same.
  opts: { readonly now?: () => number; readonly toGame?: (wireMinor: number) => number } = {},
): BalanceSync {
  const now = opts.now ?? (() => Date.now());
  const toGame = opts.toGame ?? ((m: number) => m);
  let unconfirmed = false; // the SDK's held balance may not include the last round
  let askedAt = Number.NEGATIVE_INFINITY;

  const show = (): void => {
    if (game.S.rolling || unconfirmed) return;
    const held = sdk.getBalance();
    if (held === null) return;
    const shown = toGame(held.amountMinor);
    if (shown === game.S.balance) return;
    game.S.balance = shown;
    // An autobet run between two rolls checked its next stake (now `S.stake`) against the balance the board had. The
    // game's own gate refuses a roll the balance does not cover, and nothing in the game ends a run for that: it would
    // say "Running" for ever, with nothing in flight and nothing queued. So the run ends here, the way the game ends
    // one that ran out.
    if (game.S.auto && game.S.stake > game.S.balance) game.stopAuto("Out of funds");
    else game.paint();
  };

  const ask = (): void => {
    if (now() - askedAt < ASK_EVERY_MS) return;
    askedAt = now();
    // The answer comes back as `balance.updated` (below). The SDK's promise never rejects; if it ever did, a balance
    // that was not re-read is not a failure of the game.
    void sdk.refreshBalance().catch(() => undefined);
  };

  sdk.on("balance.updated", () => {
    unconfirmed = false; // a balance landed: what the SDK holds is the server's again
    show();
  });

  // The roll landed, or was refused: the game takes its mark off #frame once its own arithmetic for the round is done.
  const frame = doc.getElementById("frame");
  if (frame) {
    new win.MutationObserver(() => {
      if (frame.hasAttribute("data-rolling")) return;
      if (unconfirmed) ask();
      show();
    }).observe(frame, { attributes: true, attributeFilter: ["data-rolling"] });
  }

  // The game's rail speaks and the stake is above the shown balance: the cashier may have closed since.
  const rail = doc.getElementById("betMsg");
  if (rail) {
    new win.MutationObserver(() => {
      if (rail.textContent !== "" && game.S.stake > game.S.balance) ask();
    }).observe(rail, { childList: true, characterData: true, subtree: true });
  }

  doc.addEventListener("visibilitychange", () => {
    if (doc.visibilityState === "visible") ask();
  });
  win.addEventListener("focus", ask);
  win.addEventListener("pageshow", (event) => {
    if ((event as PageTransitionEvent).persisted) ask();
  });

  return {
    roundSettled(round): void {
      if (round.balanceStale === true) unconfirmed = true;
    },
    ask,
  };
}
